<script setup lang="ts">
import InboxChannelPicker, { type InboxChannelOption } from './InboxChannelPicker.vue';
import InboxMessageComposer from './InboxMessageComposer.vue';
import MessageAttachmentDraftList from './MessageAttachmentDraftList.vue';
import MessageAttachmentProjectSheet from './MessageAttachmentProjectSheet.vue';
import MessageConversationSurface from './MessageConversationSurface.vue';
import { useMessageResponseFollowUp } from '@/composables/messages/useMessageResponseFollowUp';
import { toast } from 'vue-sonner';
import { useMessageAttachments } from '~/composables/messages/useMessageAttachments';
import { canSendMessageDraft } from '~/lib/messageAttachments';
import type { Message, MessageAttachment, Project } from '~/types/models';

const props = withDefaults(defineProps<{
    clientId: string;
    clientName: string;
    projects?: Project[];
    canSend?: boolean;
    title?: string;
    description?: string;
    fillHeight?: boolean;
}>(), {
    projects: () => [],
    canSend: true,
    title: '',
    description: 'Reply as the workspace and keep the full client thread visible in one place.',
    fillHeight: false,
});

const emit = defineEmits<{
    messageSent: [];
}>();

const messagesStore = useMessagesStore();
const {
    responseActionLoadingId,
    markResponseNeeded,
    sendResponseReminder,
    resolveResponse,
} = useMessageResponseFollowUp();
const tenantStore = useTenantStore();
const filesStore = useFilesStore();
const route = useRoute();
const draft = ref('');
const sending = ref(false);
const localError = ref('');
const sourceFilter = ref<InboxChannelOption['id']>('all');
const projectAttachment = ref<MessageAttachment | null>(null);
const projectAttachmentMessageId = ref('');
const projectAttachmentSheetOpen = ref(false);
const linkingAttachment = ref(false);

const threadId = computed(() => props.clientId ? `client:${props.clientId}` : '');
const messages = computed<Message[]>(() => (
    threadId.value ? messagesStore.messagesByThread[threadId.value] || [] : []
));
const visibleMessages = computed(() => (
    sourceFilter.value === 'all'
        ? messages.value
        : messages.value.filter(message => (message.channel || 'app') === sourceFilter.value)
));
const sourceOptions = computed<InboxChannelOption[]>(() => {
    const counts = { app: 0, email: 0, whatsapp: 0 };
    for (const message of messages.value) {
        const channel = message.channel || 'app';
        if (channel === 'email' || channel === 'whatsapp') counts[channel] += 1;
        else counts.app += 1;
    }
    return [
        { id: 'all', label: 'All', count: messages.value.length },
        { id: 'email', label: 'Email', count: counts.email },
        { id: 'whatsapp', label: 'WhatsApp', count: counts.whatsapp },
        { id: 'app', label: 'App', count: counts.app },
    ];
});
const messageAttachments = useMessageAttachments({
    clientId: () => props.clientId,
    tenantId: () => String(route.params.tenantSlug || tenantStore.currentTenantId || ''),
});

async function loadThread() {
    if (!props.clientId) return;
    localError.value = '';
    try {
        await messagesStore.fetchThread(props.clientId);
    } catch (error: any) {
        localError.value = error?.message || 'Could not load this conversation.';
    }
}

async function sendMessage() {
    if (!props.canSend || !canSendMessageDraft(draft.value, messageAttachments.readyAttachments.value.length)) return;
    if (messageAttachments.uploading.value || messageAttachments.hasErrors.value) return;

    localError.value = '';
    sending.value = true;
    try {
        await messagesStore.sendMessage({
            clientId: props.clientId,
            clientName: props.clientName,
            content: draft.value,
            direction: 'tenant_to_client',
            attachments: messageAttachments.readyAttachments.value,
        });
        draft.value = '';
        messageAttachments.clearDrafts();
        emit('messageSent');
    } catch (error: any) {
        localError.value = error?.message || 'Could not send this message.';
    } finally {
        sending.value = false;
    }
}

function openProjectAttachmentSheet(messageId: string, attachment: MessageAttachment) {
    projectAttachmentMessageId.value = messageId;
    projectAttachment.value = attachment;
    projectAttachmentSheetOpen.value = true;
}

async function addAttachmentToProject(projectId: string) {
    if (!projectAttachment.value || !projectAttachmentMessageId.value) return;
    linkingAttachment.value = true;
    try {
        await filesStore.linkMessageAttachmentToProject(
            projectAttachment.value,
            props.clientId,
            projectId,
            projectAttachmentMessageId.value,
        );
        toast.success('Attachment added to Project files.');
        projectAttachmentSheetOpen.value = false;
    } catch (error: any) {
        toast.error(error?.message || 'Could not add the attachment to Project files.');
    } finally {
        linkingAttachment.value = false;
    }
}

watch(() => props.clientId, async (clientId, previousClientId) => {
    if (clientId === previousClientId) return;
    if (messageAttachments.drafts.value.length) {
        await Promise.all(messageAttachments.drafts.value.map(item => messageAttachments.removeDraft(item.id)));
    }
    sourceFilter.value = 'all';
    await loadThread();
}, { immediate: true });
</script>

<template>
    <section :class="fillHeight ? 'flex h-full min-h-0 flex-1 flex-col gap-3 p-6' : 'flex min-h-[38rem] flex-col gap-3 lg:min-h-[44rem]'">
        <p v-if="localError || messagesStore.messagesError" class="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
            {{ localError || messagesStore.messagesError }}
        </p>

        <MessageConversationSurface
            :class="fillHeight ? 'min-h-0 flex-1' : ''"
            :messages="visibleMessages"
            :message-count="messages.length"
            :loading="messagesStore.loadingByThread[threadId]"
            eyebrow="Workspace inbox"
            :title="title || `Conversation with ${clientName}`"
            :description="description"
            outgoing-direction="tenant_to_client"
            :empty-title="messages.length ? 'No messages match this filter' : 'No messages yet'"
            :empty-description="messages.length ? 'Choose another source to see the rest of this client thread.' : 'Send the first message to begin the client conversation.'"
            :can-add-attachments-to-project="Boolean(projects.length && canSend)"
            :can-manage-response-follow-ups="canSend"
            :response-action-loading-id="responseActionLoadingId"
            @add-attachment-to-project="openProjectAttachmentSheet"
            @mark-response-needed="markResponseNeeded"
            @send-response-reminder="sendResponseReminder"
            @resolve-response="resolveResponse"
        />

        <InboxMessageComposer
            v-if="canSend && !messagesStore.loadingByThread[threadId]"
            v-model="draft"
            :disabled="sending"
            :sending="sending"
            :has-attachments="messageAttachments.readyAttachments.value.length > 0"
            :attachments-disabled="messageAttachments.drafts.value.length >= 5"
            @send="sendMessage"
            @files-selected="messageAttachments.addFiles"
        >
            <template #attachments>
                <MessageAttachmentDraftList
                    :drafts="messageAttachments.drafts.value"
                    :disabled="sending"
                    @remove="messageAttachments.removeDraft"
                    @retry="messageAttachments.retryDraft"
                />
            </template>
            <InboxChannelPicker v-model="sourceFilter" :options="sourceOptions" />
        </InboxMessageComposer>
        <p v-else-if="!canSend" class="rounded-lg border border-border/70 bg-muted/35 px-4 py-3 text-sm text-muted-foreground">
            You have read-only access to this conversation.
        </p>
    </section>

    <MessageAttachmentProjectSheet
        v-model:open="projectAttachmentSheetOpen"
        :attachment="projectAttachment"
        :projects="projects"
        :saving="linkingAttachment"
        @confirm="addAttachmentToProject"
    />
</template>
